<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  service: { type: Object, required: true },
});

const { t } = useI18n();

const copiedField = ref('');

const endpoint = computed(
  () => `${window.location.origin}/api/v1/otp/deliveries`
);

const curlExample = computed(
  () =>
    `curl -X POST ${endpoint.value} \\\n` +
    `  -H "X-Otp-Token: ${props.service.api_key}" \\\n` +
    `  -H "Content-Type: application/json" \\\n` +
    `  -d '{"phone_number": "+9647900000000", "message": "Your verification code: 123456"}'`
);

async function copy(field, value) {
  await copyTextToClipboard(value);
  copiedField.value = field;
  setTimeout(() => (copiedField.value = ''), 1500);
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-11">
        {{ $t('OTP_MESSAGES.API_KEY') }}
      </span>
      <div class="flex items-center gap-2">
        <code
          class="px-3 py-2 rounded-lg bg-n-alpha-3 text-sm text-n-slate-12 break-all"
        >
          {{ service.api_key }}
        </code>
        <button
          class="shrink-0 size-8 flex items-center justify-center rounded-lg hover:bg-n-alpha-2"
          :title="t('OTP_MESSAGES.API_KEY')"
          @click="copy('key', service.api_key)"
        >
          <span
            :class="
              copiedField === 'key'
                ? 'i-lucide-circle-check text-n-teal-10'
                : 'i-lucide-copy'
            "
            class="size-4"
          />
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-11">
        {{ $t('OTP_MESSAGES.API_ENDPOINT') }}
      </span>
      <div class="flex items-center gap-2">
        <code
          class="px-3 py-2 rounded-lg bg-n-alpha-3 text-sm text-n-slate-12 break-all"
        >
          {{ endpoint }}
        </code>
        <button
          class="shrink-0 size-8 flex items-center justify-center rounded-lg hover:bg-n-alpha-2"
          :title="t('OTP_MESSAGES.API_ENDPOINT')"
          @click="copy('endpoint', endpoint)"
        >
          <span
            :class="
              copiedField === 'endpoint'
                ? 'i-lucide-circle-check text-n-teal-10'
                : 'i-lucide-copy'
            "
            class="size-4"
          />
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <span class="text-sm font-medium text-n-slate-11">
        {{ $t('OTP_MESSAGES.CURL_EXAMPLE') }}
      </span>
      <pre
        class="p-3 rounded-lg bg-n-alpha-3 text-xs text-n-slate-12 overflow-auto"
        dir="ltr"
      >{{ curlExample }}</pre>
    </div>
  </div>
</template>
